import {registerGlobalInitFunc} from '../modules/observer.ts';

type Kind = 'user' | 'org';

type Network = {
  id: string;
  label: string;
  placeholder: string;
  orgPlaceholder?: string;
  fullUrl?: boolean;
};

const NETWORKS: Network[] = [
  {id: 'x', label: 'X', placeholder: 'username'},
  {id: 'linkedin', label: 'LinkedIn', placeholder: 'username', orgPlaceholder: 'company-slug'},
  {id: 'instagram', label: 'Instagram', placeholder: 'username'},
  {id: 'youtube', label: 'YouTube', placeholder: '@handle'},
  {id: 'github', label: 'GitHub', placeholder: 'username'},
  {id: 'bluesky', label: 'Bluesky', placeholder: 'handle.bsky.social'},
  {id: 'mastodon', label: 'Mastodon', placeholder: 'https://instance/@user', fullUrl: true},
  {id: 'twitch', label: 'Twitch', placeholder: 'username'},
  {id: 'facebook', label: 'Facebook', placeholder: 'username'},
  {id: 'tiktok', label: 'TikTok', placeholder: '@username'},
];

const IG_SKIP = new Set(['p', 'reel', 'reels', 'stories', 'explore', 'accounts']);
const YT_SKIP = new Set(['watch', 'shorts', 'embed', 'live', 'feed', 'playlist']);
const GH_SKIP = new Set(['settings', 'login', 'orgs', 'marketplace', 'topics', 'notifications', 'new', 'pulls', 'issues', 'explore']);

function stripAt(s: string): string {
  return s.replace(/^@/, '').replace(/\/+$/, '');
}

function parsePaste(raw: string): {id: string, handle: string} | null {
  const s = raw.trim();
  if (!s) return null;
  let url: URL;
  try {
    url = new URL(s.includes('://') ? s : `https://${s}`);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, '').toLowerCase();
  const parts = url.pathname.split('/').filter(Boolean);
  const last = stripAt(parts[parts.length - 1] || '');
  if (host === 'x.com' || host === 'twitter.com' || host === 'mobile.twitter.com') {
    const h = stripAt(parts[0] || '');
    if (!h || h === 'home' || h === 'i' || h === 'intent') return null;
    return {id: 'x', handle: h};
  }
  if (host === 'linkedin.com' || host.endsWith('.linkedin.com')) {
    if (parts[0] === 'company') return {id: 'linkedin', handle: stripAt(parts[1] || '')};
    if (parts[0] === 'in') return {id: 'linkedin', handle: stripAt(parts[1] || '')};
    return last ? {id: 'linkedin', handle: last} : null;
  }
  if (host === 'instagram.com') {
    const h = stripAt(parts[0] || '');
    if (!h || IG_SKIP.has(h)) return null;
    return {id: 'instagram', handle: h};
  }
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    let handle = '';
    if (parts[0] === 'c' || parts[0] === 'channel' || parts[0] === 'user' || parts[0] === '@') {
      handle = parts[1] || '';
    } else if (parts[0]?.startsWith('@')) {
      handle = parts[0];
    } else if (parts[0] && !YT_SKIP.has(parts[0])) {
      handle = parts[0];
    }
    handle = stripAt(handle);
    return handle ? {id: 'youtube', handle} : null;
  }
  if (host === 'github.com') {
    const h = stripAt(parts[0] || '');
    if (!h || GH_SKIP.has(h)) return null;
    return {id: 'github', handle: h};
  }
  if (host === 'bsky.app') {
    const h = stripAt((parts[0] === 'profile' ? parts[1] : parts[0]) || '');
    return h ? {id: 'bluesky', handle: h} : null;
  }
  if (host === 'twitch.tv') {
    const h = stripAt(parts[0] || '');
    if (!h || h === 'directory' || h === 'videos') return null;
    return {id: 'twitch', handle: h};
  }
  if (host === 'facebook.com' || host === 'fb.com' || host === 'fb.me') {
    if (parts[0] === 'profile.php') {
      const id = url.searchParams.get('id');
      return id ? {id: 'facebook', handle: id} : null;
    }
    const h = stripAt(parts[0] || '');
    if (!h || h === 'share' || h === 'watch' || h === 'reel') return null;
    return {id: 'facebook', handle: h};
  }
  if (host === 'tiktok.com') {
    const h = stripAt(parts[0] || '');
    if (!h || h === 'foryou' || h === 'video') return null;
    return {id: 'tiktok', handle: h};
  }
  if (parts[0]?.startsWith('@')) {
    return {id: 'mastodon', handle: `${url.origin}/${parts[0]}`};
  }
  return null;
}

function initProfileSocials(rootEl: HTMLElement): void {
  const kind = (rootEl.getAttribute('data-kind') || 'user') as Kind;
  const removeLabel = rootEl.getAttribute('data-remove-label') || 'Remove';
  const list = rootEl.querySelector<HTMLElement>('.socials-list')!;
  const select = rootEl.querySelector<HTMLSelectElement>('.socials-network')!;
  const input = rootEl.querySelector<HTMLInputElement>('.socials-handle')!;
  const addBtn = rootEl.querySelector<HTMLButtonElement>('.socials-add-btn')!;
  const bank = rootEl.querySelector<HTMLElement>('.socials-icon-bank')!;
  const hiddenWrap = rootEl.querySelector<HTMLElement>('.socials-hidden')!;

  const values = new Map<string, string>();
  for (const el of hiddenWrap.querySelectorAll<HTMLInputElement>('input[type="hidden"]')) {
    const id = el.name.replace(/^social_/, '');
    if (el.value) values.set(id, el.value);
  }

  function iconFor(id: string): Node {
    const src = bank.querySelector(`[data-network="${id}"]`);
    return src ? src.cloneNode(true) : document.createTextNode('');
  }

  function syncHidden(): void {
    hiddenWrap.replaceChildren();
    for (const net of NETWORKS) {
      const inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = `social_${net.id}`;
      inp.value = values.get(net.id) || '';
      hiddenWrap.append(inp);
    }
    for (const opt of select.options) {
      if (!opt.value) continue;
      opt.disabled = values.has(opt.value);
    }
  }

  function render(): void {
    list.replaceChildren();
    for (const net of NETWORKS) {
      const handle = values.get(net.id);
      if (!handle) continue;
      const li = document.createElement('li');
      li.className = 'socials-row-edit';
      const icon = document.createElement('span');
      icon.className = 'social-icon';
      icon.dataset.network = net.id;
      icon.append(iconFor(net.id));
      const label = document.createElement('span');
      label.textContent = `${net.label} · ${handle}`;
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'ui tiny basic button';
      rm.textContent = removeLabel;
      rm.addEventListener('click', () => {
        values.delete(net.id);
        render();
      });
      li.append(icon, label, rm);
      list.append(li);
    }
    syncHidden();
  }

  function addFromFields(): void {
    const id = select.value;
    let handle = input.value.trim();
    const parsed = parsePaste(handle);
    const net = NETWORKS.find((n) => n.id === (parsed?.id || id));
    if (!net) return;
    if (parsed) handle = parsed.handle;
    if (!handle) return;
    if (!net.fullUrl) handle = stripAt(handle);
    values.set(net.id, handle);
    input.value = '';
    select.value = '';
    render();
  }

  input.addEventListener('paste', (e) => {
    const text = e.clipboardData?.getData('text') || '';
    const parsed = parsePaste(text);
    if (!parsed) return;
    e.preventDefault();
    select.value = parsed.id;
    input.value = parsed.handle;
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFromFields();
    }
  });

  addBtn.addEventListener('click', (e) => {
    e.preventDefault();
    addFromFields();
  });

  select.addEventListener('change', () => {
    const net = NETWORKS.find((n) => n.id === select.value);
    if (!net) return;
    input.placeholder = kind === 'org' && net.orgPlaceholder ? net.orgPlaceholder : net.placeholder;
  });

  render();
}

export function initProfileSocialsFeature(): void {
  registerGlobalInitFunc('initProfileSocials', initProfileSocials);
}
